import { Component, OnInit, TemplateRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import {
  CommercialRow,
  LogicielCaisseCommercialService,
} from 'app/services/logiciel-caisse/logiciel-caisse-commercial.service';
import {
  LogicielCaisseEntrepotOption,
  LogicielCaisseEntrepotService,
} from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';

@Component({
  selector: 'app-logiciel-caisse-commercial',
  templateUrl: './logiciel-caisse-commercial.component.html',
  styleUrls: ['./logiciel-caisse-commercial.component.scss'],
})
export class LogicielCaisseCommercialComponent implements OnInit {
  loading = false;
  saving = false;
  rows: CommercialRow[] = [];
  filteredRows: CommercialRow[] = [];
  searchTerm = '';
  editId: number | null = null;
  form: UntypedFormGroup;
  entrepotOptions: LogicielCaisseEntrepotOption[] = [];

  constructor(
    private readonly commercialService: LogicielCaisseCommercialService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly modalService: NgbModal,
    private readonly fb: UntypedFormBuilder,
    private readonly toastr: ToastrService
  ) {
    this.form = this.fb.group({
      prenom: ['', Validators.required],
      nom: ['', Validators.required],
      entrepot_id: [null as string | null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.load();
  }

  commercialLabel(row: CommercialRow): string {
    return `${row.prenom ?? ''} ${row.nom ?? ''}`.trim() || `#${row.id}`;
  }

  entrepotLabel(row: CommercialRow): string {
    return row.entrepot_libelle?.trim() || (row.entrepot_id ? `Entrepôt ${row.entrepot_id}` : '—');
  }

  load(): void {
    this.loading = true;
    forkJoin({
      options: this.entrepotService.getEntrepotOptions(),
      commerciaux: this.commercialService.listForCurrentStation(),
    }).subscribe({
      next: ({ options, commerciaux }) => {
        this.entrepotOptions = options;
        this.rows = commerciaux;
        this.applySearch();
        this.loading = false;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
        this.loading = false;
      },
    });
  }

  applySearch(): void {
    const kw = this.searchTerm.trim().toLowerCase();
    if (!kw) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter((row) => {
        const blob = `${this.commercialLabel(row)} ${this.entrepotLabel(row)}`.toLowerCase();
        return blob.includes(kw);
      });
    }
    this.refreshFeatherIcons();
  }

  openCreate(modal: TemplateRef<unknown>): void {
    this.editId = null;
    this.form.reset();
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  openEdit(row: CommercialRow, modal: TemplateRef<unknown>): void {
    this.editId = row.id;
    this.form.patchValue({
      nom: row.nom,
      prenom: row.prenom,
      entrepot_id: row.entrepot_id != null ? String(row.entrepot_id) : null,
    });
    this.modalService.open(modal, { size: 'lg', centered: true });
  }

  save(modalRef: { close: () => void }): void {
    if (this.form.invalid) {
      return;
    }
    const nom = String(this.form.value.nom ?? '').trim();
    const prenom = String(this.form.value.prenom ?? '').trim();
    const entrepotId = this.form.value.entrepot_id;

    let payload;
    try {
      payload = this.commercialService.buildPayload(entrepotId, nom, prenom);
    } catch (err) {
      this.toastr.error(err instanceof Error ? err.message : 'Données invalides.');
      return;
    }

    this.saving = true;

    if (this.editId) {
      this.commercialService.update(this.editId, payload).subscribe({
        next: () => {
          this.saving = false;
          modalRef.close();
          this.toastr.success('Commercial enregistré.');
          this.load();
        },
        error: (err: Error) => {
          this.saving = false;
          this.toastr.error(err.message);
        },
      });
      return;
    }

    this.commercialService.create(payload).subscribe({
      next: () => {
        this.saving = false;
        modalRef.close();
        this.toastr.success('Commercial créé.');
        this.load();
      },
      error: (err: Error) => {
        this.saving = false;
        this.toastr.error(err.message);
      },
    });
  }

  deleteRow(row: CommercialRow): void {
    if (!confirm(`Supprimer le commercial « ${this.commercialLabel(row)} » ?`)) {
      return;
    }
    this.commercialService.delete(row.id).subscribe({
      next: () => {
        this.toastr.success('Commercial supprimé.');
        this.load();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
      },
    });
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      if (typeof (window as unknown as { feather?: { replace: () => void } }).feather?.replace === 'function') {
        (window as unknown as { feather: { replace: () => void } }).feather.replace();
      }
    }, 0);
  }
}
